import type { ReactNode } from 'react'

import { Button } from '../../ui/Control'
import { RichText } from '../../ui/RichText'
import { Switch } from '../../ui/Switch'
import { TagInput } from './TagInput'

/**
 * What somebody is typing when they make or change a flashcard.
 *
 * Both sides say which markup they are written in, because they are written in the one rich-text
 * input like everything else — see `work/47-eine-rich-text-eingabe.md`. They are *two* markups and
 * not one: a front that is a single sentence and a back that is a table with footnotes is the
 * ordinary case, and forcing one language on both would be forcing the long one on the short side.
 */
export interface CardEntry {
  readonly front: string
  readonly back: string
  readonly frontFormat: string
  readonly backFormat: string
  /** The plain-text cue the typing drill compares against. Deliberately not marked up. */
  readonly askedFor: string
  readonly tags: readonly string[]
  readonly reversible: boolean
}

/**
 * The one form a flashcard is made and changed in.
 *
 * There used to be two — one in the card area and one in a session of the timetable — written
 * independently, which is how one of them came to be a pair of bare text fields with no markup
 * choice and no reverse switch. Whatever is added to card entry is added here and is therefore
 * everywhere; that is the whole reason this file exists rather than being inlined twice.
 *
 * It is **controlled**: the draft and the tag being typed live with the caller, because the card
 * area needs them for its duplicate lookup and for opening an existing card in it. What this owns is
 * the arrangement and nothing else.
 */
export function CardForm({
  draft,
  onDraft,
  knownTags,
  tagText,
  onTagText,
  busy = false,
  submitLabel,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  /**
   * Remounts both editors.
   *
   * CodeMirror keeps its own document, so a draft that was cleared behind it has to be built again —
   * changing this is what says "this is a different card now".
   */
  editorKey = 0,
  autoFocus = false,
  children,
}: {
  readonly draft: CardEntry
  readonly onDraft: (draft: CardEntry) => void
  readonly knownTags: readonly string[]
  readonly tagText: string
  readonly onTagText: (value: string) => void
  readonly busy?: boolean
  readonly submitLabel: string
  readonly onSubmit: () => void
  readonly onCancel?: () => void
  readonly cancelLabel?: string
  readonly editorKey?: number
  readonly autoFocus?: boolean
  /** Shown under the form — where the card area puts the cards that look like this one. */
  readonly children?: ReactNode
}) {
  const ready = draft.front.trim() !== '' && draft.back.trim() !== ''
  const change = (part: Partial<CardEntry>) => {
    onDraft({ ...draft, ...part })
  }

  return (
    <>
      <div className="card-write__sides">
        <RichText
          key={`front-${String(editorKey)}`}
          text={draft.front}
          format={draft.frontFormat}
          onChange={(front) => {
            change({ front })
          }}
          onFormat={(frontFormat) => {
            change({ frontFormat })
            return Promise.resolve()
          }}
          label="Front"
          placeholder="Question or prompt"
          initially="text"
          purpose="flashcard-front"
          autoFocus={autoFocus}
          onSubmit={onSubmit}
          controlsInTabOrder={false}
        >
          <strong>Front</strong>
        </RichText>
        <RichText
          key={`back-${String(editorKey)}`}
          text={draft.back}
          format={draft.backFormat}
          onChange={(back) => {
            change({ back })
          }}
          onFormat={(backFormat) => {
            change({ backFormat })
            return Promise.resolve()
          }}
          label="Back"
          placeholder="Answer"
          initially="text"
          purpose="flashcard-back"
          onSubmit={onSubmit}
          controlsInTabOrder={false}
        >
          <strong>Back</strong>
        </RichText>
      </div>
      <div className="card-write__meta">
        <label className="card-field card-write__asked-for">
          <span className="card-field__label">Asked for on the front</span>
          {/*
            Plain text on purpose, and the one field here that is: it is what the typing drill
            compares against, character for character, so a markup in it would break the comparison
            rather than decorate it.
          */}
          <input
            className="card-field__control"
            value={draft.askedFor}
            maxLength={160}
            placeholder="e.g. definition or source"
            onChange={(event) => {
              change({ askedFor: event.target.value })
            }}
          />
        </label>
        <TagInput
          value={tagText}
          selected={draft.tags}
          known={knownTags}
          onChange={onTagText}
          onAdd={(raw) => {
            const wanted = raw.trim()
            if (wanted !== '' && !draft.tags.includes(wanted)) {
              change({ tags: [...draft.tags, wanted] })
            }
            onTagText('')
          }}
          onRemove={(tag) => {
            change({ tags: draft.tags.filter((one) => one !== tag) })
          }}
        />
        <Switch
          label="Ask both sides"
          hint="Front and back each become a prompt."
          on={draft.reversible}
          onChange={(reversible) => {
            change({ reversible })
          }}
        />
        <div className="card-write__actions">
          {onCancel !== undefined && <Button onClick={onCancel}>{cancelLabel}</Button>}
          <Button tone="primary" disabled={busy || !ready} onClick={onSubmit}>
            {submitLabel}
          </Button>
        </div>
      </div>
      {children}
    </>
  )
}
